import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

export type BeautyNomination = components["schemas"]["BeautyNomination"];
export type BeautyNominationInput = components["schemas"]["BeautyNominationInput"];
export type BlindBeautyNomination = components["schemas"]["BlindBeautyNomination"];

export const beautyKeys = {
  /** Nằm dưới `orders` để mọi lệnh sửa đơn tự làm mới ô này. */
  one: (orderId: string) => ["orders", "beauty-nomination", orderId] as const,
  /** Theo mã bài chấm: QC chấm mù, không cầm mã đơn. */
  blind: (queueId: number) => ["performance", "blind-beauty", queueId] as const,
};

/**
 * Đơn này đã được đề cử chưa.
 *
 * `null` là câu trả lời BÌNH THƯỜNG (chưa ai đề cử), không phải lỗi — màn
 * hình dùng nó để chọn giữa nút "Khách đẹp" và nút "Gỡ đề cử".
 *
 * `enabled` để chỗ gọi tắt hẳn truy vấn khi người dùng không thuộc phòng được
 * đề cử: hỏi rồi lĩnh 403 chỉ để giấu nút là ba lượt gọi thừa mỗi lần mở đơn.
 */
export function useBeautyNomination(orderId: string, enabled: boolean) {
  return useQuery({
    queryKey: beautyKeys.one(orderId),
    enabled: enabled && !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/beauty-nomination", {
        params: { path: { id: orderId } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đọc được đề cử Khách đẹp"));
      return data.nomination ?? null;
    },
    staleTime: 60_000,
  });
}

export function useNominateBeauty(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BeautyNominationInput) => {
      const { data, error } = await api.POST("/orders/{id}/beauty-nomination", {
        params: { path: { id: orderId } },
        body: input,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gửi được đề cử"));
      return data.nomination;
    },
    onSuccess: (n) => {
      // Ghi thẳng câu trả lời vào ô nhớ thay vì chỉ đánh dấu cũ: nút phải đổi
      // sang "Đã đề cử" NGAY, không chờ một lượt gọi mạng nữa.
      qc.setQueryData(beautyKeys.one(orderId), n);
      refreshAlbumTags(qc, orderId);
    },
  });
}

export function useWithdrawBeauty(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string) => {
      const { data, error } = await api.DELETE("/orders/{id}/beauty-nomination", {
        params: { path: { id: orderId } },
        body: { reason },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gỡ được đề cử"));
      return data.nomination;
    },
    onSuccess: (n) => {
      qc.setQueryData(beautyKeys.one(orderId), n);
      refreshAlbumTags(qc, orderId);
    },
  });
}

/**
 * Đề cử theo mã bài ở màn Chấm bài (CEO chốt 6a, 31/10/2026). Đề cử của người khác
 * chỉ trả `mine: false`, không có tên hay nội dung.
 */
export function useBlindBeauty(queueId: number) {
  return useQuery({
    queryKey: beautyKeys.blind(queueId),
    queryFn: async () => {
      const { data, error } = await api.GET("/performance/review-queue/beauty", {
        params: { query: { queueId } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đọc được đề cử Khách đẹp"));
      return data.nomination ?? null;
    },
    staleTime: 60_000,
  });
}

export function useNominateBlindBeauty(queueId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BeautyNominationInput) => {
      const { data, error } = await api.POST("/performance/review-queue/beauty", {
        params: { query: { queueId } },
        body: input,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gửi được đề cử"));
      return data.nomination ?? null;
    },
    onSuccess: (n) => qc.setQueryData(beautyKeys.blind(queueId), n),
  });
}

export function useWithdrawBlindBeauty(queueId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (_reason: string) => {
      const { data, error } = await api.DELETE("/performance/review-queue/beauty", {
        params: { query: { queueId } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gỡ được đề cử"));
      return data.nomination ?? null;
    },
    onSuccess: (n) => qc.setQueryData(beautyKeys.blind(queueId), n),
  });
}

/**
 * Máy chủ gắn/gỡ nhãn album "Khách đẹp" cùng lượt ghi đề cử (QĐ 0279), nên
 * cây album và ô nhãn của kho ảnh phải hỏi lại — không thì chấm nhãn trên cây
 * vẫn nói như trước khi bấm. Khoá ghi tay thay vì nhập `galleryKeys`: tệp
 * api của kho ảnh kéo theo cả khối kho ảnh vào mọi màn có nút đề cử.
 */
function refreshAlbumTags(qc: ReturnType<typeof useQueryClient>, orderId: string) {
  void qc.invalidateQueries({ queryKey: ["gallery", "order", orderId] });
  void qc.invalidateQueries({ queryKey: ["gallery", "album-tags"] });
}
